import { useEffect, useState } from 'react';

import { isReviewApp } from 'app/lib/environmentChecker';

const getCookiebot = () =>
  typeof window !== 'undefined' ? (window as any).Cookiebot : undefined;

export const useCookiebotConsent = () => {
  const shouldRequireCookiebotConsent =
    process.env.NODE_ENV === 'production' && !isReviewApp();

  // consentReady is true only after the user has made an active choice,
  // or a prior choice has been loaded from cookie (returning user).
  // Important: we use hasResponse rather than checking consent existence because
  // Cookiebot initialises the consent object before the user interacts, which would
  // incorrectly mark consent as ready for new users. Cookiebot may also not exist
  // yet, e.g. when this runs above withCookieBot on the first render.
  const [consentReady, setConsentReady] = useState<boolean>(
    () => !shouldRequireCookiebotConsent || !!getCookiebot()?.hasResponse
  );
  const [hasStatisticsConsent, setHasStatisticsConsent] = useState<boolean>(
    () =>
      !shouldRequireCookiebotConsent || !!getCookiebot()?.consent?.statistics
  );
  const [hasMarketingConsent, setHasMarketingConsent] = useState<boolean>(
    () => !shouldRequireCookiebotConsent || !!getCookiebot()?.consent?.marketing
  );

  useEffect(() => {
    // Cookiebot may have already fired before this effect registered listeners
    // (eg script loaded from cache for a returning visitor). Check synchronously
    // on mount so we don't miss that case
    if (shouldRequireCookiebotConsent && getCookiebot()?.hasResponse) {
      setConsentReady(true);
      setHasStatisticsConsent(!!getCookiebot()?.consent?.statistics);
      setHasMarketingConsent(!!getCookiebot()?.consent?.marketing);
    } else if (!shouldRequireCookiebotConsent) {
      setConsentReady(true);
      setHasStatisticsConsent(true);
      setHasMarketingConsent(true);
    }

    const handleConsentReady = () => {
      setConsentReady(true);
      setHasStatisticsConsent(!!getCookiebot()?.consent?.statistics);
      setHasMarketingConsent(!!getCookiebot()?.consent?.marketing);
    };

    window.addEventListener('CookiebotOnConsentReady', handleConsentReady);
    window.addEventListener('CookiebotOnAccept', handleConsentReady);
    window.addEventListener('CookiebotOnDecline', handleConsentReady);

    return () => {
      window.removeEventListener('CookiebotOnConsentReady', handleConsentReady);
      window.removeEventListener('CookiebotOnAccept', handleConsentReady);
      window.removeEventListener('CookiebotOnDecline', handleConsentReady);
    };
  }, [shouldRequireCookiebotConsent]);

  return {
    consentReady,
    statisticsConsent: hasStatisticsConsent,
    marketingConsent: hasMarketingConsent,
  };
};
